import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LimitedEditionNumberDto } from '../../api-client';

// The pool lives in the store and not in the components because the same numbers are needed by the product
// detail form, by the cart drawer and by the checkout: keeping it here means one call per product instead of
// one per place that shows it.
interface LimitedEditionState {
	/**
	 * Selectable numbers indexed by sap code. A sap code with no entry has not been loaded yet; a sap code
	 * with an empty array has no number configured, which is what tells the form to keep the free text field.
	 */
	numbersBySapCode: Record<string, LimitedEditionNumberDto[]>;
	/** Products already asked for, so reopening a form does not go to the backend again. */
	loadedProductIds: number[];
	loading: boolean;
}

const initialState: LimitedEditionState = {
	numbersBySapCode: {},
	loadedProductIds: [],
	loading: false,
};

export const limitedEditionSlice = createSlice({
	name: 'limitedEdition',
	initialState,
	reducers: {
		setLimitedEditionsLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},

		/**
		 * Stores what a product offers. The sap codes of the product are passed alongside the numbers so that
		 * a size with no number left still gets an empty array, told apart from a size never loaded.
		 */
		initProductLimitedEditions: (
			state,
			action: PayloadAction<{
				productId: number;
				sapCodes: string[];
				numbers: LimitedEditionNumberDto[];
			}>,
		) => {
			action.payload.sapCodes.forEach((sapCode) => {
				state.numbersBySapCode[sapCode] = [];
			});

			action.payload.numbers.forEach((entry) => {
				if (!entry.sapCode) return;

				const pool = state.numbersBySapCode[entry.sapCode] ?? [];
				pool.push(entry);
				state.numbersBySapCode[entry.sapCode] = pool;
			});

			if (!state.loadedProductIds.includes(action.payload.productId))
				state.loadedProductIds.push(action.payload.productId);

			state.loading = false;
		},

		/**
		 * Forgets everything. Used when the customer changes, and after a conflict: what is on screen is stale
		 * and has to come back from the backend.
		 */
		clearLimitedEditions: (state) => {
			state.numbersBySapCode = {};
			state.loadedProductIds = [];
			state.loading = false;
		},
	},
});

export const {
	setLimitedEditionsLoading,
	initProductLimitedEditions,
	clearLimitedEditions,
} = limitedEditionSlice.actions;

export default limitedEditionSlice.reducer;
